/**
 * ScenarioSkillPrepareDialog —— 进入场景前的关联技能装配确认（web 版）。
 *
 * 触发时机：用户点击场景卡片，且该场景关联的技能有本地缺失项。全部已装时 App 不会
 * 挂载本组件，直接进场景（否则每次进场景都被弹一次）。
 *
 * 三个阶段：
 *   confirm    列出缺失技能，[取消] / [安装并进入]
 *   installing 每项实时显示安装中 / 已就绪 / 失败
 *   failed     有失败项时停下，[重试] / [跳过并进入]（跳过后该技能不生效）
 *
 * 安装失败不堵人：用户永远能选择跳过进入场景，只是那个技能不生效。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog } from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  NoSymbolIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { setSkills } from '../../store/slices/skillSlice';
import type { MyScenario } from '../../services/scenario';
import SkillIcon from '../icons/SkillIcon';
import ScenarioAppIcon from './ScenarioAppIcon';
import {
  ensureScenarioSkills,
  type ScenarioSkillEntry,
  type ScenarioSkillState,
} from '../../services/scenarioSkills';

interface Props {
  scenario: MyScenario;
  /** 首次探测结果，至少包含一个 missing 项。 */
  entries: ScenarioSkillEntry[];
  /** 用户放弃进入场景。 */
  onCancel: () => void;
  /** 装配结束（全部成功或用户选择跳过）→ 进入场景。 */
  onProceed: () => void;
}

type Phase = 'confirm' | 'installing' | 'failed';

const StateBadge: React.FC<{ state: ScenarioSkillState }> = ({ state }) => {
  if (state === 'ready') {
    return (
      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
        <CheckCircleIcon className="w-4 h-4" />
        {i18nService.t('scenarioSkillStateReady')}
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
        <ExclamationCircleIcon className="w-4 h-4" />
        {i18nService.t('scenarioSkillStateFailed')}
      </span>
    );
  }
  if (state === 'unavailable') {
    return (
      <span className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
        <ExclamationCircleIcon className="w-4 h-4" />
        {i18nService.t('scenarioSkillStateUnavailable')}
      </span>
    );
  }
  if (state === 'disabled') {
    return (
      <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
        <NoSymbolIcon className="w-4 h-4" />
        {i18nService.t('scenarioSkillStateDisabled')}
      </span>
    );
  }
  if (state === 'installing') {
    return (
      <span className="flex items-center gap-1 text-xs text-claude-accent dark:text-claude-accent">
        <motion.span
          className="w-3 h-3 rounded-full border-2 border-current border-t-transparent"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
        />
        {i18nService.t('scenarioSkillPrepareInstalling')}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
      <ArrowDownTrayIcon className="w-4 h-4" />
      {i18nService.t('scenarioSkillStateMissing')}
    </span>
  );
};

const ScenarioSkillPrepareDialog: React.FC<Props> = ({ scenario, entries, onCancel, onProceed }) => {
  const dispatch = useDispatch();
  const reducedMotion = useReducedMotion();
  const requestRef = useRef<AbortController | null>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const [requestError, setRequestError] = useState(false);
  useEffect(() => {
    const cancel = () => { requestRef.current?.abort(); cancelRef.current(); };
    window.addEventListener('auth:identityChanging', cancel);
    return () => { requestRef.current?.abort(); window.removeEventListener('auth:identityChanging', cancel); };
  }, []);
  const [phase, setPhase] = useState<Phase>('confirm');
  // slug（原样，未归一化）→ 当前状态。安装期间由 onProgress 实时驱动。
  const [stateBySlug, setStateBySlug] = useState<Record<string, ScenarioSkillState>>(() => {
    const initial: Record<string, ScenarioSkillState> = {};
    entries.forEach(e => { initial[e.link.skill_name] = e.state; });
    return initial;
  });
  const [errorBySlug, setErrorBySlug] = useState<Record<string, string>>({});

  // unavailable 不算"待处理"——它无解，按钮再点也装不上
  const pendingCount = useMemo(
    () => entries.filter(e => e.state !== 'ready' && e.state !== 'unavailable').length,
    [entries],
  );
  // 全是"已装但被禁用"时，这次动作只是翻开关，不下载任何东西，按钮文案要如实反映
  const onlyNeedsEnable = useMemo(
    () => entries.some(e => e.state === 'disabled') && entries.every(e => e.state !== 'missing'),
    [entries],
  );
  // 一个都处理不了（只剩平台已下架的）：没什么可"安装"的，只能让用户知情后继续
  const nothingActionable = pendingCount === 0;

  /** 同步锁阻止双击；错误可重试，卸载/换账号后停止队列且不再同步 Redux。 */
  const runInstall = useCallback(async () => {
    if (requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setPhase('installing'); setRequestError(false); setErrorBySlug({});
    try {
      const result = await ensureScenarioSkills(scenario, {
        signal: controller.signal,
        onProgress: (slug, state, error) => {
          if (controller.signal.aborted) return;
          setStateBySlug(prev => ({ ...prev, [slug]: state }));
          if (error) setErrorBySlug(prev => ({ ...prev, [slug]: error }));
        },
      });
      if (controller.signal.aborted) return;
      dispatch(setSkills(result.localSkills));
      setStateBySlug(Object.fromEntries(result.entries.map(entry => [entry.link.skill_name, entry.state])));
      if (result.failed.length === 0) onProceed();
      else setPhase('failed');
    } catch {
      if (!controller.signal.aborted) { setPhase('failed'); setRequestError(true); }
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [scenario, dispatch, onProceed]);

  const busy = phase === 'installing';

  return (
    <Dialog open onClose={() => { if (!busy) onCancel(); }} className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 p-4 backdrop-blur-[2px]">
      <Dialog.Panel as={motion.div}
        initial={reducedMotion ? false : { opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex w-full max-w-[560px] flex-col overflow-hidden rounded-2xl border border-claude-border bg-claude-surface shadow-xl sm:flex-row dark:border-claude-darkBorder dark:bg-claude-darkSurface"
      >
        <aside className="flex shrink-0 items-center gap-3 border-b border-claude-borderLight bg-claude-surfaceHover/50 px-5 py-4 sm:w-32 sm:flex-col sm:justify-center sm:border-b-0 sm:border-r sm:px-4 dark:border-claude-darkBorderLight dark:bg-claude-darkSurfaceHover/35">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-claude-border bg-claude-surface shadow-sm dark:border-claude-darkBorder dark:bg-claude-darkSurface">
            <ScenarioAppIcon scenario={scenario} size={28} />
          </div>
          <div className="min-w-0 text-left sm:text-center">
            <div className="line-clamp-2 text-[13px] font-semibold leading-5 text-claude-text dark:text-claude-darkText">
              {scenario.name}
            </div>
            {scenario.group_name && (
              <div className="mt-0.5 truncate text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                {scenario.group_name}
              </div>
            )}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="px-5 pb-3 pt-5 pr-14">
            <Dialog.Title as="h2"
              id="scenario-skill-dialog-title"
              className="text-[17px] font-semibold text-claude-text dark:text-claude-darkText"
            >
              {i18nService.t('scenarioSkillPrepareTitle')}
            </Dialog.Title>
            {requestError && <p role="alert" className="mt-2 text-sm text-red-600">{i18nService.t('scenarioDetailsLoadFailed')}</p>}
            <p className="mt-1 text-[13px] leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {phase === 'failed'
                ? i18nService.t('scenarioSkillPrepareFailedHint')
                : i18nService.t('scenarioSkillPrepareDesc')}
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text disabled:cursor-not-allowed disabled:opacity-30 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
            title={i18nService.t('scenarioSkillPrepareCancel')}
            aria-label={i18nService.t('scenarioSkillPrepareCancel')}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>

          <div className="max-h-[46vh] overflow-y-auto px-5 pb-4 pt-1">
            <ul className="divide-y divide-claude-borderLight border-y border-claude-borderLight dark:divide-claude-darkBorderLight dark:border-claude-darkBorderLight">
              {entries.map(entry => {
                const slug = entry.link.skill_name;
                const state = stateBySlug[slug] ?? entry.state;
                const error = errorBySlug[slug];
                return (
                  <li
                    key={entry.link.skill_id}
                    className="flex min-h-14 items-center justify-between gap-3 py-2.5"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <SkillIcon className="h-5 w-5 shrink-0 text-claude-accent" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-claude-text dark:text-claude-darkText">
                          {entry.link.display_name || slug}
                        </div>
                        {entry.link.display_name && entry.link.display_name !== slug && !error && (
                          <div className="mt-0.5 truncate font-mono text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                            {slug}
                          </div>
                        )}
                        {error && (
                          <div className="mt-0.5 line-clamp-2 text-xs text-red-500 dark:text-red-400" title={error}>
                            {error}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      <StateBadge state={state} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex min-h-16 items-center justify-end gap-2 border-t border-claude-borderLight px-5 py-3 dark:border-claude-darkBorderLight">
            {phase === 'failed' ? (
              <>
                <button
                  type="button"
                  onClick={onProceed}
                  className="h-9 rounded-lg px-3.5 text-sm text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                >
                  {i18nService.t('scenarioSkillPrepareSkip')}
                </button>
                <button
                  type="button"
                  onClick={runInstall}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-claude-accent px-4 text-sm font-medium text-white transition-colors hover:bg-claude-accent/90"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  {i18nService.t('scenarioSkillPrepareRetry')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={busy}
                  className="h-9 rounded-lg px-3.5 text-sm text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text disabled:cursor-not-allowed disabled:opacity-40 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                >
                  {i18nService.t('scenarioSkillPrepareCancel')}
                </button>
                <button
                  type="button"
                  onClick={nothingActionable ? onProceed : runInstall}
                  disabled={busy}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-claude-accent px-4 text-sm font-medium text-white transition-colors hover:bg-claude-accent/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy
                    ? <ArrowPathIcon className="h-4 w-4 animate-spin motion-reduce:animate-none" />
                    : nothingActionable
                      ? <ArrowRightIcon className="h-4 w-4" />
                      : <ArrowDownTrayIcon className="h-4 w-4" />}
                  {busy
                    ? i18nService.t('scenarioSkillPrepareInstalling')
                    : nothingActionable
                      ? i18nService.t('scenarioSkillPrepareContinue')
                      : `${i18nService.t(onlyNeedsEnable ? 'scenarioSkillPrepareEnable' : 'scenarioSkillPrepareInstall')}${pendingCount > 1 ? ` (${pendingCount})` : ''}`}
                </button>
              </>
            )}
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};

export default ScenarioSkillPrepareDialog;
